import type {RefInfo} from '@github-ui/repos-types'
import React, {useMemo} from 'react'

/**
 * Core RefInfo without edit permission fields.
 * canEdit, canEditOnDefaultBranch, and fileExistsOnDefault are in EditPermissionsContext
 * since they change more frequently.
 */
export type CoreRefInfo = Omit<RefInfo, 'canEdit' | 'canEditOnDefaultBranch' | 'fileExistsOnDefault'>

const RefInfoContext = React.createContext<CoreRefInfo | null>(null)

export function RefInfoProvider({children, refInfo}: React.PropsWithChildren<{refInfo?: RefInfo}>) {
  const value = useMemo(
    () => ({
      name: refInfo?.name ?? '',
      listCacheKey: refInfo?.listCacheKey ?? '',
      refType: refInfo?.refType,
      currentOid: refInfo?.currentOid ?? '',
    }),
    [refInfo?.name, refInfo?.listCacheKey, refInfo?.refType, refInfo?.currentOid],
  )

  return <RefInfoContext value={value}>{children}</RefInfoContext>
}

export function useRefInfo(): CoreRefInfo {
  const context = React.use(RefInfoContext)
  if (context === null) {
    throw new Error('useRefInfo must be used within a RefInfoProvider')
  }
  return context
}
